import type { FC, ReactNode } from 'react'
import React from 'react'
import type { IconButtonProps } from '@material-ui/core'
import { IconButton } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import type { SvgIconProps } from '@material-ui/core'
import { SvgIcon } from '@material-ui/core'
import clsx from 'clsx'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: theme.spacing(4.75),
      height: theme.spacing(4.75),
      borderRadius: '5px',
      fontSize: 14,
    },
    inner: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    outlined: {
      border: '1px solid #788292',
    },
    disabled: { border: '1px solid #CED4D9', pointerEvents: 'none' },
    transparent: {
      border: 'none',
    },
  }),
)
interface IProps extends IconButtonProps {
  svgIcon?: string
  variant?: 'transparent' | 'outlined'
  rotateIcon?: number
  customBgColor?: string
  className?: string
  disabled?: boolean
  fontSize?: number
  svgSource?: 'image' | 'inline'
  children?: ReactNode
}

const iconTypes = {
  arrow: <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />,
  menu: <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />,
  close: (
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
  ),
}

function Icon(props: SvgIconProps) {
  return <SvgIcon {...props}>{props.children}</SvgIcon>
}

const CustomIconButton: FC<IProps> = ({
  children = null,
  variant,
  svgIcon,
  customBgColor = 'transparent',
  rotateIcon = 0,
  className,
  disabled,
  fontSize = 18,
  svgSource = 'inline',
  ...rest
}) => {
  const classes = useStyles()

  let builtInIcon = iconTypes?.[svgIcon]

  if (svgSource === 'image') {
    builtInIcon = <img src={`assets/icons/${svgIcon}.svg`} alt="Icon button" />
  }

  return (
    <IconButton
      className={clsx(
        classes.root,
        variant === 'outlined' && classes.outlined,
        variant === 'transparent' && classes.transparent,
        disabled && classes.disabled,
        className,
      )}
      style={{
        backgroundColor: customBgColor,
      }}
      {...rest}
    >
      <div
        className={classes.inner}
        style={{
          transform: `rotate(${rotateIcon}deg)`,
        }}
      >
        {builtInIcon && svgSource === 'inline' && (
          <Icon
            fill={disabled ? '#CED4D9' : '#788292'}
            style={{ fontSize: fontSize }}
          >
            {builtInIcon}
          </Icon>
        )}
        {builtInIcon && svgSource === 'image' && builtInIcon}
        {children && children}
      </div>
    </IconButton>
  )
}

export default CustomIconButton
